<script setup lang="ts">
import {
  OrButtonV3 as OrButton,
  OrInputV3 as OrInput,
  OrModalV3 as OrModal,
  OrNotificationV3 as OrNotification,
} from '@onereach/ui-components';
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { ref, computed } from 'vue';

import passwordApi from '../../api/password';
import useI18n from '../../composables/useI18n';

const { translate } = useI18n();

const props = defineProps<{
  redirectPath: string | null;
}>();

const resetError = ref('');
const resetEmail = ref('');
const prevEmail = ref('');
const passwordLoading = ref(false);
const forgotPasswordEmailSent = ref(false);

const rules = computed(() => ({
  resetEmail: {
    required,
    email,
  },
}));

const v$ = useVuelidate(rules, { resetEmail });

const passwordForgotStart = async () => {
  resetError.value = '';
  passwordLoading.value = true;

  if (v$.value.resetEmail.$invalid) {
    if (v$.value.resetEmail.required.$invalid) {
      resetError.value = translate('login.password_restore.This field is required');
    } else if (v$.value.resetEmail.email.$invalid) {
      resetError.value = translate('login.password_restore.Invalid value of email');
    }
    passwordLoading.value = false;
    return;
  }

  forgotPasswordEmailSent.value = false;

  const redirectPath = props.redirectPath;

  await passwordApi.startResetPassword(resetEmail.value, redirectPath)
    .then(() => {
      forgotPasswordEmailSent.value = true;
    })
    .catch(err => {
      const errorMessage = err.cause?.response?.data?.message;
      resetError.value = errorMessage || 'Something went wrong - please try later';
    });

  prevEmail.value = resetEmail.value;
  resetEmail.value = '';
  passwordLoading.value = false;
};

const resend = async () => {
  passwordLoading.value = true;
  const redirectPath = props.redirectPath;
  await passwordApi.startResetPassword(prevEmail.value, redirectPath)
    .catch(err => {
      const errorMessage = err.cause?.response?.data?.message;
      resetError.value = errorMessage || 'Something went wrong - please try later';
    });
  passwordLoading.value = false;
};

const onCloseResetPassword = () => {
  resetError.value = '';
  resetEmail.value = '';
  forgotPasswordEmailSent.value = false;
};

const root = ref<typeof OrModal | null>(null);

const open = () => {
  root.value?.open();
};

defineExpose({
  open,
});

</script>

<template>
  <OrModal
    ref="root"
    :remove-close-button="true"
    :title="$t('login.login.resetPassword')"
    size="s"
    class="reset-modal"
    :full-screen="false"
    @close="onCloseResetPassword"
  >
    <template #header>
      <div>{{ $t('login.login.resetPassword') }}</div>
    </template>
    <template #default>
      <div class="layout-column gap-md">
        <OrNotification
          v-if="forgotPasswordEmailSent"
          variant="success"
          disable-close
        >
          <span>{{ $t('login.login.Instructions have been successfully sent to') }} <span class="typography-body-2-bold"> {{ prevEmail }}</span>.&nbsp;</span>
          <OrButton
            variant="link"
            class="!typography-body-2-bold"
            @click="resend"
          >
            {{ $t('login.login.resend') }}
          </OrButton>
        </OrNotification>
        <div>
          <p>{{ $t('login.login.enterYourEmail') }}</p>
        </div>
        <OrInput
          v-model.trim="resetEmail"
          :error="resetError"
          :label="$t('login.login.emailAddress')"
          :placeholder="$t('session_expire.Enter email')"
          class="username"
          name="username"
          type="text"
          @keyup.enter.native="passwordForgotStart"
        />
      </div>
    </template>
    <template #footer>
      <div class="layout-row gap-md justify-end">
        <OrButton
          variant="outlined"
          class="flex-1 md:!flex-none"
          :disabled="passwordLoading"
          @click.prevent="root?.close()"
        >
          {{ $t('login.login.cancel') }}
        </OrButton>
        <OrButton
          class="flex-1 md:!flex-none"
          color="primary"
          :loading="passwordLoading"
          @click.prevent="passwordForgotStart"
        >
          {{ $t('login.login.submit') }}
        </OrButton>
      </div>
    </template>
  </OrModal>
</template>
